import React, { Fragment, useEffect, useState } from "react";
import Layout from "../../components/Layout/Layout";
import DateRangePicker from "../../components/DateRangePicker/DateRangePicker";
import receivedDocument from "../../assets/images/receivedReport.svg";
import rejectedDocument from "../../assets/images/rejectedReport.svg";
import DocumentButton from "../../components/DocumentButton/DocumentButton";
import DownloadOrange from "../../assets/images/DownloadOrange.svg";
import BlueHomeDot from "../../assets/images/BlueHomeDot.svg";
import BlueHomeDotDisable from "../../assets/images/BlueHomeDotDisable.svg";
import RedHomeDotDisable from "../../assets/images/RedHomeDotDisable.svg";
import RedHomeDot from "../../assets/images/RedHomeDot.svg";
import { Area, AreaChart, XAxis, Tooltip, YAxis } from "recharts";
import ViewUploadedMenu from "../../components/ViewUploadedMenu/ViewUploadedMenu";
import CheckBox from "../../components/CheckBox/CheckBox";
import ApiService from "../../services/ApiService";
import moment from "moment";
import { IAppointmentEvent, IBarChartStatistics, IDoctor, IStatistics } from "../../helpers/Interfaces";

import "./Home.scss";

export default function Home() {

  const apiService = new ApiService();

  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState({
    fromDate: moment(),
    toDate: moment(),
  });
  const [showAlert, setShowAlert] = useState({
    isError: false,
    message: "",
    open: false,
  });
  const [showStatus, setShowStatus] = useState({
    reports: { recevied: true, pending: true },
  });
  const [statistics, setStatistics] = useState({} as IStatistics);
  const [chartStatistics, setChartStatistics] = useState(
    {} as IBarChartStatistics
  );
  const [appointments, setAppointments] = useState([] as IAppointmentEvent[]);
  const [doctors, setDoctors] = useState<IDoctor[]>([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState<string | null>(null);

  useEffect(() => {
    getStatistics();
    getChartStatistics();
    getAppointments();
    getAssigned();
  }, []);

  useEffect(() => {
    getChartStatistics();
  }, [tabValue]);

  function getAppointments() {

    setLoading(true);

    let data = {
      appointmentStartDate: moment.utc().local(),
      appointmentEndDate: moment.utc().local(),
    };

    apiService.getAppointmentByDateRange(data).then((result: any) => {

      let data = result.data;

      if (!data?.isSuccessful) {
        let newShowAlert = showAlert;
        newShowAlert.isError = true;
        newShowAlert.message = data?.errorMessage;
        newShowAlert.open = true;
        setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
        return;
      }

      getNextAppointments(data?.appointments[0]?.events);
    }).catch(() => {
      let newShowAlert = showAlert;
      newShowAlert.isError = true;
      newShowAlert.message = "Unexpected Error";
      newShowAlert.open = true;
      setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
    }).finally(() => {
      setLoading(false);
    });
  }

  function getStatistics() {

    setLoading(true);

    let data = {
      fromDate: formData.fromDate,
      toDate: formData.toDate,
    };

    apiService.getDocumentStatistics(data).then((result) => {

      let data = result.data;

      if (!data?.isSuccessful) {
        let newShowAlert = showAlert;
        newShowAlert.isError = true;
        newShowAlert.message = data?.errorMessage;
        newShowAlert.open = true;
        setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
        setLoading(false);
        return;
      }

      setStatistics(data?.statistics);
    }).catch(() => {
      let newShowAlert = showAlert;
      newShowAlert.isError = true;
      newShowAlert.message = "Unexpected Error";
      newShowAlert.open = true;
      setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
    }).finally(() => {
      setLoading(false);
    });
  }

  function getChartStatistics() {

    setLoading(true);

    let data = {
      period: tabValue === 0 ? "Monthly" : "Yearly",
    };

    apiService.getDocumentBarChartStatistics(data).then((result) => {
      let data = result.data;

      if (!data?.isSuccessful) {
        let newShowAlert = showAlert;
        newShowAlert.isError = true;
        newShowAlert.message = data?.errorMessage;
        newShowAlert.open = true;
        setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
        setLoading(false);
        return;
      }

      let newChartStatistics = chartStatistics;
      newChartStatistics.statistics = data?.statistics;
      setChartStatistics(newChartStatistics);
    }).catch(() => {
      let newShowAlert = showAlert;
      newShowAlert.isError = true;
      newShowAlert.message = "Unexpected Error";
      newShowAlert.open = true;
      setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
    }).finally(() => {
      setLoading(false);
    });
  }

  function getAssigned() {

    setLoading(true);

    apiService.getAssignedDoctors().then((result) => {

      let data = result.data;

      if (!data?.isSuccessful) {
        let newShowAlert = showAlert;
        newShowAlert.isError = true;
        newShowAlert.message = data?.errorMessage;
        newShowAlert.open = true;
        setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
        setLoading(false);
        return;
      }

      const assigned: IDoctor[] = data?.assigned;

      setDoctors(assigned);
      setSelectedDoctorId(assigned[0].doctorId);
    }).catch(() => {
      let newShowAlert = showAlert;
      newShowAlert.isError = true;
      newShowAlert.message = "Unexpected Error";
      newShowAlert.open = true;
      setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
    }).finally(() => {
      setLoading(false);
    });
  }

  function getNextAppointments(appointments: IAppointmentEvent[]) {
    let nextAppointments = [] as IAppointmentEvent[];
    let currentDateTime = moment();

    appointments.forEach((appointment) => {
      if (moment.utc(appointment.startTime).local().isAfter(currentDateTime)) {
        nextAppointments.push(appointment);
      }
    });

    setAppointments(nextAppointments);
  }

  return (
    <Layout selectedIndex={0}>
      <div className="home-container">
        <div className="top-bar container-header animated animatedFadeInUp fadeInUp">
          <div className="row">
            <span className="location">Home</span>
          </div>
        </div>
        <div className="home-content content-container">
          <div className="select-clinic-wrapper main-content-gap animated animatedFadeInUp fadeInUp">
            {doctors.map((doctor) => {
              return (
                <div className={`select-clinic ${selectedDoctorId === doctor.doctorId ? 'selected' : ''}`}
                  onClick={() => { setSelectedDoctorId(doctor.doctorId); }}>
                  <div>
                    <span className="left">Doctor: </span>
                  </div>
                  <div>
                    <span>{doctor.doctor}</span>
                  </div>
                </div>
              )
            })}
          </div>
          <div className="report-detail-container main-content-gap animated animatedFadeInUp fadeInUp">
            <div className="report-sum-board">
              <div className="sum-state-board">
                <div className="report-box received-color conteiner-gap">
                  <div className="report-sub-box">
                    <img src={receivedDocument} alt="recieved" />
                    <div className="detail-box">
                      <div className="report-count">
                        {statistics.received ? statistics.received : 0} /{" "}
                        {statistics.total ? statistics.total : 0}
                      </div>
                      <div className="report">Received Reports</div>
                    </div>
                  </div>
                </div>
                <div className="report-box rejected-color">
                  <div className="report-sub-box">
                    <img src={rejectedDocument} alt="recieved" />
                    <div className="detail-box">
                      <div className="report-count">
                        {statistics.pending ? statistics.pending : 0} /{" "}
                        {statistics.total ? statistics.total : 0}
                      </div>
                      <div className="report">Pending Reports</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="date-ranger-container">
                <div className="date-title">Date Range*</div>
                <DateRangePicker
                  startPlaceHolder="FROM"
                  endPlaceHolder="TO"
                  onChange={(startDate, endDate) => {
                    let newFormData = formData;
                    newFormData.fromDate = moment.utc(startDate);
                    newFormData.toDate = moment.utc(endDate);
                    setFormData((prevDetails) => ({
                      ...prevDetails,
                      ...newFormData,
                    }));
                  }}
                />
              </div>
              <div className="report-buttont">
                <DocumentButton
                  text={"Search"}
                  loading={loading}
                  onClick={() => {
                    getStatistics();
                  }}
                />
              </div>
            </div>
            {/* <div className="vatical-line"></div> */}
            <div className="download-main-content">
              <img src={DownloadOrange} alt="download" />
              <div className="download-sub-content">
                <div className="download-title">Download</div>
                <div className="download-report">Full Report</div>
              </div>
            </div>
          </div>
          <div className="report-statistics-main-container animated animatedFadeInUp fadeInUp">
            <div className="statistics-content">
              <div className="graph animated animatedFadeInUp fadeInUp">
                <div className="graph-header">
                  <span className="header">Reports Statistics</span>
                  <div className="report-select-content">
                    <div className="home-report-select">
                      <div className="report-row">
                        <span className="report-name">Received</span>
                        <CheckBox
                          icon={BlueHomeDotDisable}
                          checkedIcon={BlueHomeDot}
                          checked={showStatus.reports.recevied}
                          onChange={(data: any) => {
                            let newShowStatus = showStatus;
                            newShowStatus.reports.recevied = data;
                            setShowStatus((prevDetails) => ({
                              ...prevDetails,
                              ...newShowStatus,
                            }));
                          }}
                        />
                      </div>
                      <div className="report-row">
                        <span className="report-name">Pending</span>
                        <CheckBox
                          icon={RedHomeDotDisable}
                          checkedIcon={RedHomeDot}
                          checked={showStatus.reports.pending}
                          onChange={(data: any) => {
                            let newShowStatus = showStatus;
                            newShowStatus.reports.pending = data;
                            setShowStatus((prevDetails) => ({
                              ...prevDetails,
                              ...newShowStatus,
                            }));
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="menu-content">
                    <ViewUploadedMenu
                      tabValue={tabValue}
                      tabValueHandler={(value: number) => {
                        setTabValue(value);
                      }}
                      array={["Monthly", "Yearly"]}
                    />
                  </div>
                </div>
                <AreaChart
                  width={window.innerWidth * 0.6}
                  height={260}
                  data={chartStatistics.statistics}
                  margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#203FAD" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#203FAD" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorPv" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#EC7430" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#EC7430" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <YAxis
                    color="transparent"
                    width={20}
                    axisLine={false}
                    style={{ opacity: "0" }}
                  />
                  <XAxis
                    tickLine={false}
                    dataKey="label"
                    style={{
                      fontSize: "10px",
                      fontWeight: "700",
                      opacity: "0.5",
                    }}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    contentStyle={{ border: "10" }}
                  />
                  {showStatus.reports.recevied ? (
                    <Area
                      type="monotone"
                      dataKey="recevied"
                      stroke="#203FAD"
                      fillOpacity={1}
                      fill="url(#colorUv)"
                    />
                  ) : (
                    <Fragment />
                  )}
                  {showStatus.reports.pending ? (
                    <Area
                      type="monotone"
                      dataKey="pending"
                      stroke="#EC7430"
                      fillOpacity={1}
                      fill="url(#colorPv)"
                    />
                  ) : (
                    <Fragment />
                  )}
                </AreaChart>
              </div>
              <div className="appointment-detail-container animated animatedFadeInUp fadeInUp">
                <div className="appointment-detail-sub-container">
                  <div className="appointment-title">
                    Next Appointment Details
                  </div>
                  <div className="appointment-main-conteiner animated animatedFadeInUp fadeInUp">
                    {appointments.length > 0 ? (
                      <div className="next-appointment-card-content">
                        <div className="next-appointment-sub-content">
                          <div className="appointment-name next-appointment-font">
                            {appointments[0].clinicName} -{" "}
                            {appointments[0].patientName}
                          </div>
                          <div className="appintment-date-arrow-content">
                            <div className="next-appointment-date">
                              {moment(appointments[0].startTime).format("hh:mm A" + ", " + "DD-MM-YYYY")}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <Fragment />
                    )}
                    {appointments?.map((appointment, index) => {
                      if (index !== 0) {
                        return (
                          <div
                            key={appointment.appointmentId}
                            className="appointment-card-content"
                          >
                            <div className="appointment-sub-content">
                              <div className="appointment-user-name appointment-font">
                                {appointment.clinicName} -{" "}
                                {appointment.patientName}
                              </div>
                              <div className="appointment-date">
                                {moment(appointment.startTime).format("hh:mm A" + ", " + "DD-MM-YYYY")}
                              </div>
                            </div>
                          </div>
                        );
                      }
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <span className="label-value">{`Reports -  ${payload[0]?.value ? payload[0]?.value : 0
          }`}</span>
      </div>
    );
  }

  return <></>;
};
